import { Fragment, useEffect, useState } from 'react';
import {
  Box, Button, Card, Chip, Collapse, IconButton, Tab, Tabs, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, Tooltip, Typography, alpha,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api/client';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../components/ToastProvider';
import { errMessage } from '../../utils/errMessage';
import { PageHeader } from '../../components/PageHeader';

interface Pending {
  id: number; symbol: string; side: string; quantity: string; price: string | null; market: string; order_type: string;
  leverage: number | null; status: string; client_email: string | null; client_name: string | null; reject_reason: string | null;
  created_at: string; reviewed_at: string | null;
  approvals_count: number; required_approvals: number; approved_by_me: number;
  reduce_only: number; close_position_size: string | null;
  take_profit: string | null; stop_loss: string | null; position_side: string | null;
}

interface Approver { admin_id: number; admin_name: string | null; admin_email: string | null; approved_at: string; }

const TABS = [
  { label: 'Pending', status: 'PENDING' },
  { label: 'Approved', status: 'APPROVED' },
  { label: 'Rejected', status: 'REJECTED' },
  { label: 'All', status: 'ALL' },
] as const;

const statusColor: Record<string, string> = {
  PENDING: '#f5a623', APPROVED: '#16c784', REJECTED: '#ea3943', EXPIRED: '#8b97b0', CANCELLED: '#8b97b0',
};
const sc = (s: string) => statusColor[s] ?? '#8b97b0';

/** Trim trailing zeros from a DECIMAL(36,18) string for display, without precision loss. */
const fmtNum = (v: string | number | null | undefined): string => {
  if (v === null || v === undefined || v === '') return '—';
  const s = String(v);
  if (!s.includes('.')) return s;
  const t = s.replace(/0+$/, '').replace(/\.$/, '');
  return t === '' ? '0' : t;
};

/** All timestamps render in Bahrain time (Asia/Bahrain, UTC+3). */
const fmtBahrain = (v: string | null | undefined): string => {
  if (!v) return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '—';
  const s = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Asia/Bahrain', year: 'numeric', month: 'short', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
  }).format(d);
  return `${s} (Bahrain)`;
};

/** Expanded detail: the approver audit trail (who approved, when — in Bahrain time). */
function ApproverTrail({ pendingId, required }: { pendingId: number; required: number }) {
  const { data, isLoading } = useQuery({
    queryKey: ['approvals', pendingId],
    queryFn: async () => (await api.get(`/trading/orders/${pendingId}/approvals`)).data.data.items as Approver[],
  });
  const rows = data ?? [];
  return (
    <Box sx={{ px: 2, py: 1.5 }}>
      <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
        Approval trail — {rows.length} of {required} approvals
      </Typography>
      {isLoading ? (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>Loading…</Typography>
      ) : rows.length ? (
        rows.map((a, i) => (
          <Box key={a.admin_id} sx={{ display: 'flex', gap: 1.5, alignItems: 'center', py: 0.5 }}>
            <Chip size="small" label={`#${i + 1}`} sx={{ fontWeight: 700 }} />
            <Box sx={{ fontWeight: 600 }}>{a.admin_name || a.admin_email || `Admin #${a.admin_id}`}</Box>
            <Box sx={{ color: 'text.secondary', fontSize: 13 }}>{fmtBahrain(a.approved_at)}</Box>
          </Box>
        ))
      ) : (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>No approvals yet.</Typography>
      )}
    </Box>
  );
}

export default function ApprovalsPage() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const toast = useToast();
  const isSuperAdmin = user?.role === 'SUPER_ADMIN';
  const [tab, setTab] = useState(0);
  const [expanded, setExpanded] = useState<number | null>(null);
  const status = TABS[tab].status;
  const isPendingTab = status === 'PENDING';

  // Required-approvals setting for indirect-access trades (read by all staff, editable by super-admins).
  const { data: minApprovals } = useQuery({
    queryKey: ['min-trade-approvals'],
    queryFn: async () => (await api.get('/admin/settings/min-trade-approvals')).data.data.value as number,
  });
  const [approvalInput, setApprovalInput] = useState('');
  useEffect(() => { if (minApprovals != null) setApprovalInput(String(minApprovals)); }, [minApprovals]);
  const saveApprovals = useMutation({
    mutationFn: (v: number) => api.put('/admin/settings/min-trade-approvals', { value: v }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['min-trade-approvals'] });
      qc.invalidateQueries({ queryKey: ['pending'] });
    },
  });
  const inputNum = Number(approvalInput);
  const inputInvalid = !Number.isInteger(inputNum) || inputNum < 1 || inputNum > 10;

  const { data } = useQuery({
    queryKey: ['pending', status],
    queryFn: async () => (await api.get('/trading/pending', { params: { status } })).data.data.items as Pending[],
    refetchInterval: isPendingTab ? 5_000 : false,
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['pending'] });
    qc.invalidateQueries({ queryKey: ['approvals'] });
  };
  const approve = useMutation({
    mutationFn: (id: number) => api.post(`/trading/orders/${id}/approve`),
    onSuccess: () => { toast.success('Trade approval recorded', 'Approved'); invalidate(); },
    onError: (e) => toast.error(errMessage(e, 'Failed to approve'), 'Approve failed'),
  });
  const reject = useMutation({
    mutationFn: (id: number) => api.post(`/trading/orders/${id}/reject`, { reason: 'Rejected by admin' }),
    onSuccess: () => { toast.warning('Trade rejected', 'Rejected'); invalidate(); },
    onError: (e) => toast.error(errMessage(e, 'Failed to reject'), 'Reject failed'),
  });

  const rows = data ?? [];

  return (
    <Box>
      <PageHeader
        title="Trade Approvals"
        subtitle="Indirect-access trades require multiple admin approvals before they execute. Times shown in Bahrain time."
        action={
          isPendingTab && rows.length ? (
            <Chip label={`${rows.length} awaiting`}
              sx={{ fontWeight: 600, color: '#f5a623', backgroundColor: alpha('#f5a623', 0.14), border: `1px solid ${alpha('#f5a623', 0.35)}` }} />
          ) : undefined
        }
      />

      <Card sx={{ p: 2, mb: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Box sx={{ minWidth: 240 }}>
          <Typography variant="subtitle2">Approvals required for indirect-access trades</Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Number of different admins who must approve before an indirect trade executes.
          </Typography>
        </Box>
        <Box sx={{ flex: 1 }} />
        {isSuperAdmin ? (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
            <TextField
              size="small" label="Approvals" value={approvalInput}
              onChange={(e) => { if (/^\d*$/.test(e.target.value)) setApprovalInput(e.target.value); }}
              inputProps={{ inputMode: 'numeric' }} sx={{ width: 110 }}
              error={!!approvalInput && inputInvalid}
              helperText={
                saveApprovals.isError ? 'Save failed — try again'
                  : approvalInput && inputInvalid ? '1 to 10'
                  : saveApprovals.isSuccess && inputNum === minApprovals ? 'Saved' : ' '
              }
            />
            <Button
              variant="contained" sx={{ mt: 0.25 }}
              disabled={saveApprovals.isPending || inputInvalid || inputNum === minApprovals}
              onClick={() => saveApprovals.mutate(inputNum)}
            >
              Save
            </Button>
          </Box>
        ) : (
          <Chip label={`${minApprovals ?? '—'} approvals required`} sx={{ fontWeight: 700 }} />
        )}
      </Card>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        {TABS.map((t) => <Tab key={t.status} label={t.label} />)}
      </Tabs>

      <Card>
        <TableContainer>
          <Table sx={{ minWidth: 920, '& tbody tr:hover': { backgroundColor: 'rgba(255,255,255,0.03)' } }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 40 }} />
                <TableCell>Client</TableCell>
                <TableCell>Symbol</TableCell>
                <TableCell>Market</TableCell>
                <TableCell>Side</TableCell>
                <TableCell>Type</TableCell>
                <TableCell align="right">Lev</TableCell>
                <TableCell align="right">Quantity</TableCell>
                <TableCell align="right">Price</TableCell>
                <TableCell align="center">Approvals</TableCell>
                <TableCell>Requested</TableCell>
                <TableCell align="right">Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((o) => {
                const color = o.side === 'BUY' ? '#16c784' : '#ea3943';
                const c = sc(o.status);
                // Reduce-only close against a known position size: show "X out of Y".
                const fullSize = Number(o.close_position_size);
                const isClose = !!o.reduce_only && Number.isFinite(fullSize) && fullSize > 0;
                const isPartialClose = isClose && Number(o.quantity) < fullSize;
                const isTpSl = o.order_type === 'TPSL';
                const required = o.required_approvals ?? 2;
                const count = o.approvals_count ?? 0;
                const complete = count >= required;
                const progressColor = complete ? '#16c784' : '#f5a623';
                const mine = !!o.approved_by_me;
                const isOpen = expanded === o.id;
                return (
                  <Fragment key={o.id}>
                  <TableRow>
                    <TableCell>
                      <IconButton size="small" onClick={() => setExpanded(isOpen ? null : o.id)} aria-label="show approvals">
                        {isOpen ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
                      </IconButton>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ fontWeight: 600 }}>{o.client_name || '—'}</Box>
                      <Box sx={{ fontSize: 12, color: 'text.secondary' }}>{o.client_email}</Box>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{o.symbol}</TableCell>
                    <TableCell sx={{ color: 'text.secondary' }}>{o.market}</TableCell>
                    <TableCell>
                      <Chip size="small" label={o.side}
                        sx={{ color, backgroundColor: alpha(color, 0.14), border: `1px solid ${alpha(color, 0.35)}` }} />
                    </TableCell>
                    <TableCell sx={{ color: 'text.secondary' }}>{o.order_type}</TableCell>
                    <TableCell align="right" className="mono">{o.leverage ? `${o.leverage}x` : '—'}</TableCell>
                    <TableCell align="right" className="mono">
                      {fmtNum(o.quantity)}
                      {isClose && (
                        <Tooltip title={isPartialClose
                          ? `Partial close — ${fmtNum(o.quantity)} out of ${fmtNum(o.close_position_size)}`
                          : `Full close of ${fmtNum(o.close_position_size)}`}>
                          <Box sx={{ fontSize: 11, fontWeight: 700, cursor: 'default', color: isPartialClose ? '#f5a623' : '#8b97b0' }}>
                            {isPartialClose ? `of ${fmtNum(o.close_position_size)} · close` : 'full close'}
                          </Box>
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell align="right" className="mono">{fmtNum(o.price)}</TableCell>
                    <TableCell align="center">
                      <Tooltip title={complete ? 'All required approvals received' : `${required - count} more approval(s) needed`}>
                        <Chip size="small" label={`${count} / ${required}`}
                          sx={{ fontWeight: 700, color: progressColor, backgroundColor: alpha(progressColor, 0.14), border: `1px solid ${alpha(progressColor, 0.35)}` }} />
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ color: 'text.secondary', fontSize: 12, whiteSpace: 'nowrap' }}>
                      {fmtBahrain(o.created_at)}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title={o.status === 'REJECTED' && o.reject_reason ? o.reject_reason : ''} disableHoverListener={!o.reject_reason}>
                        <Chip size="small" label={o.status}
                          sx={{ color: c, backgroundColor: alpha(c, 0.14), border: `1px solid ${alpha(c, 0.35)}` }} />
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right">
                      {o.status === 'PENDING' ? (
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', alignItems: 'center' }}>
                          <Tooltip title={mine ? 'You have already approved this trade' : ''}>
                            <span>
                              <Button size="small" variant="contained" color="success" startIcon={<CheckIcon />}
                                disabled={approve.isPending || mine} onClick={() => approve.mutate(o.id)}>
                                {mine ? 'Approved' : 'Approve'}
                              </Button>
                            </span>
                          </Tooltip>
                          <Button size="small" variant="outlined" color="error" startIcon={<CloseIcon />}
                            disabled={reject.isPending} onClick={() => reject.mutate(o.id)}>Reject</Button>
                        </Box>
                      ) : (
                        <Box sx={{ fontSize: 12, color: 'text.secondary' }}>
                          {o.reviewed_at ? fmtBahrain(o.reviewed_at) : '—'}
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={13} sx={{ py: 0, borderBottom: isOpen ? undefined : 'none' }}>
                      <Collapse in={isOpen} timeout="auto" unmountOnExit>
                        {isOpen && isClose && (
                          <Box sx={{ px: 2, pt: 1.5 }}>
                            <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>Position close</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {isPartialClose
                                ? `Partial close — ${fmtNum(o.quantity)} out of ${fmtNum(o.close_position_size)} (${((Number(o.quantity) / fullSize) * 100).toFixed(1)}% of position)`
                                : `Full close of ${fmtNum(o.close_position_size)}`}
                            </Typography>
                          </Box>
                        )}
                        {isOpen && isTpSl && (
                          <Box sx={{ px: 2, pt: 1.5 }}>
                            <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>Take-profit / stop-loss update</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {o.position_side ? `${o.position_side} · ` : ''}
                              TP {o.take_profit != null ? fmtNum(o.take_profit) : '— (remove)'} · SL {o.stop_loss != null ? fmtNum(o.stop_loss) : '— (remove)'}
                            </Typography>
                          </Box>
                        )}
                        {isOpen && <ApproverTrail pendingId={o.id} required={required} />}
                      </Collapse>
                    </TableCell>
                  </TableRow>
                  </Fragment>
                );
              })}
              {!rows.length && (
                <TableRow>
                  <TableCell colSpan={13} sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>
                    {isPendingTab ? 'No pending orders. All caught up. ✨' : `No ${TABS[tab].label.toLowerCase()} orders.`}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
    </Box>
  );
}
